/**
 * Admin User Service
 * Handles all user-related admin API calls
 */

import { getApi, postApi, updateApi, deleteApi, patchApi } from '../adminApi';

/**
 * Get all users with pagination
 * @param {Object} params - { page: number, limit: number }
 * @returns {Promise}
 */
export const getUsers = async (params = {}) => {
  const { page = 1, limit = 10 } = params;
  return getApi(`users?page=${page}&limit=${limit}`);
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise}
 */
export const getUserById = async (userId) => {
  return getApi(`users/${userId}`);
};

/**
 * Register a new user (admin)
 * @param {Object} data - { email: string, password: string, name: string }
 * @returns {Promise}
 */
export const registerUser = async (data) => {
  return postApi('register-user', data);
};

/**
 * Update user details
 * @param {string} userId - User ID
 * @param {Object} data - User data to update
 * @returns {Promise}
 */
export const updateUser = async (userId, data) => {
  return updateApi(`users/${userId}`, data);
};

/**
 * Block/Unblock a user
 * @param {string} userId - User ID
 * @param {string} action - 'block' or 'unblock'
 * @param {string} reason - Reason for blocking
 * @returns {Promise}
 */
export const blockUser = async (userId, action, reason = '') => {
  return postApi(`users/${userId}/block`, { action, reason });
};

/**
 * Update user wallet balance
 * @param {string} userId - User ID
 * @param {number} amount - Amount to add/subtract
 * @returns {Promise}
 */
export const updateUserWalletBalance = async (userId, amount) => {
  return postApi(`users/${userId}/wallet/balance`, { amount });
};

/**
 * Change user password (admin)
 * @param {string} userId - User ID
 * @param {string} newPassword - New password
 * @returns {Promise}
 */
export const changeUserPassword = async (userId, newPassword) => {
  return postApi(`users/${userId}/change-password`, { newPassword });
};

/**
 * Delete a user
 * @param {string} userId - User ID
 * @returns {Promise}
 */
export const deleteUser = async (userId) => {
  return deleteApi(`users/${userId}`);
};

/**
 * Get user transactions
 * @param {string} userId - User ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getUserTransactions = async (userId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return getApi(`users/${userId}/transactions?${queryParams}`);
};

/**
 * Get user trades
 * @param {string} userId - User ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getUserTrades = async (userId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return getApi(`users/${userId}/trades?${queryParams}`);
};

